import { sleep } from "@/lib/utils";

import { applyGroupDiscount, ideaProductIds } from "@osmix/ui-primitives/idea-editor";
import type { Api, Idea, IdeaProductSummary } from "../types";
import { products as productsApi } from "./products";

const photo = (id: string) =>
	`https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1000&q=80`;

// A small spread of seller ideas across the moderation lifecycle so the page is
// meaningful offline: two awaiting review, two published (one curated into the
// feed), and one rejected.
let ideas: Idea[] = [
	{
		id: "idea_demo_1",
		shopId: "shop-handmade",
		shopName: "Мастерская Анны",
		title: "Тёплый уголок ручной работы",
		description:
			"Текстиль, керамика и украшения ручной работы, собранные в один уютный образ для дома.",
		type: "inspiration",
		status: "published",
		images: [
			{
				id: "iimg_1a",
				url: photo("1513161455079-7dc1de15ef3e"),
				pins: [
					{ id: "p1", x: 32, y: 38, productId: "prd_demo_3" },
					{ id: "p2", x: 68, y: 56, productId: "prd_demo_4" },
				],
			},
			{
				id: "iimg_1b",
				url: photo("1522708323590-d24dbb6b0267"),
				pins: [{ id: "p3", x: 50, y: 45, productId: "prd_demo_5" }],
			},
		],
		productCount: 3,
		feedRank: 0,
		audience: ["home"],
		moderatorNote: "Хорошие фото, товары видны",
		createdAt: "2026-05-10T10:00:00Z",
		updatedAt: "2026-05-10T10:00:00Z",
	},
	{
		id: "idea_demo_2",
		shopId: "shop-handmade",
		shopName: "Мастерская Анны",
		title: "Летние находки",
		description: "Лёгкие аксессуары и украшения на каждый день — подборка к новому сезону.",
		type: "seasonal",
		status: "moderation",
		images: [
			{
				id: "iimg_2a",
				url: photo("1483985988355-763728e1935b"),
				pins: [
					{ id: "p4", x: 40, y: 35, productId: "prd_demo_3" },
					{ id: "p5", x: 62, y: 64, productId: "prd_demo_5" },
				],
			},
		],
		productCount: 2,
		feedRank: null,
		createdAt: "2026-06-01T09:30:00Z",
		updatedAt: "2026-06-01T09:30:00Z",
	},
	{
		id: "idea_demo_3",
		shopId: "shop-keramika",
		shopName: "Керамика Плюс",
		title: "Керамика для кухни",
		description: "Посуда ручной лепки — от утренней чашки до большого блюда для гостей.",
		type: "inspiration",
		status: "moderation",
		images: [
			{
				id: "iimg_3a",
				url: photo("1578749556568-bc2c40e68b61"),
				pins: [{ id: "p6", x: 48, y: 52, productId: "prd_demo_4" }],
			},
		],
		productCount: 1,
		feedRank: null,
		createdAt: "2026-06-08T14:15:00Z",
		updatedAt: "2026-06-08T14:15:00Z",
	},
	{
		id: "idea_demo_4",
		shopId: "shop-handmade",
		shopName: "Мастерская Анны",
		title: "Подарки до 5 000 ₽",
		description: "Небольшие, но особенные вещи ручной работы для близких.",
		type: "collaboration",
		status: "published",
		images: [
			{
				id: "iimg_4a",
				url: photo("1512909006721-3d6018887383"),
				pins: [{ id: "p7", x: 55, y: 40, productId: "prd_demo_5" }],
			},
		],
		productCount: 1,
		feedRank: null,
		createdAt: "2026-05-18T12:00:00Z",
		updatedAt: "2026-05-18T12:00:00Z",
	},
	{
		id: "idea_demo_5",
		shopId: "shop-keramika",
		shopName: "Керамика Плюс",
		title: "Черновик без фото",
		description: "Пример отклонённой идеи: не хватает товаров с метками.",
		type: "inspiration",
		status: "rejected",
		images: [
			{
				id: "iimg_5a",
				url: photo("1493663284031-b7e3aefcae8e"),
				pins: [{ id: "p8", x: 50, y: 50, productId: "prd_demo_4" }],
			},
		],
		productCount: 1,
		rejectionReason: "Добавьте больше товаров и уточните описание подборки.",
		moderatorNote: "Добавьте больше товаров и уточните описание подборки.",
		feedRank: null,
		createdAt: "2026-05-22T16:40:00Z",
		updatedAt: "2026-05-22T16:40:00Z",
	},
];

// То же обогащение, что делает шлюз: товары в порядке меток, суммы, метрики
// (офлайн — нули: событий витрины в моке нет).
const enrich = async (idea: Idea): Promise<Idea> => {
	const ids = ideaProductIds(idea.images);
	const found = await Promise.all(ids.map((id) => productsApi.get(id).catch(() => null)));
	const products: IdeaProductSummary[] = found
		.filter((p) => p !== null)
		.map((p) => ({
			id: p.id,
			name: p.name,
			article: p.article,
			brand: p.brand,
			photo: p.photos[0],
			color: p.color,
			sizes: p.sizes,
			price: p.price,
			oldPrice: p.oldPrice && p.oldPrice > p.price ? p.oldPrice : undefined,
			shopId: p.shopId,
			status: p.status,
		}));
	const totalPrice = products.reduce((s, p) => s + p.price, 0);
	return {
		...idea,
		productCount: ids.length,
		products,
		totalPrice,
		discountedTotal: applyGroupDiscount(totalPrice, idea.groupDiscount ?? null),
		metrics: idea.metrics ?? { reach: 0, impressions: 0, productClicks: 0, favorites: 0, cartAdds: 0 },
	};
};

const find = (id: string): Idea => {
	const idea = ideas.find((i) => i.id === id && i.status !== "archived");
	if (!idea) throw new Error("Idea not found");
	return idea;
};

const save = async (next: Idea): Promise<Idea> => {
	ideas = ideas.map((i) => (i.id === next.id ? next : i));
	return enrich(next);
};

const touch = (idea: Idea, patch: Partial<Idea>): Idea => ({
	...idea,
	...patch,
	updatedAt: new Date().toISOString(),
});

export const ideasApi: Api["ideas"] = {
	async page(filter) {
		await sleep(150);
		const slice = filter.list ?? "all";
		const rows = ideas
			.filter((i) => i.status !== "archived")
			.filter((i) =>
				slice === "moderation" ? i.status === "moderation"
				: slice === "blocked" ? i.status === "blocked"
				: slice === "unpublished" ? i.status !== "published" && i.status !== "blocked"
				: true,
			)
			.filter((i) => !filter.shopId || i.shopId === filter.shopId)
			.sort((a, b) => (filter.dir === "asc" ? 1 : -1) * a.createdAt.localeCompare(b.createdAt));
		const start = (filter.page - 1) * filter.pageSize;
		return {
			items: rows.slice(start, start + filter.pageSize),
			total: rows.length,
			page: filter.page,
			pageSize: filter.pageSize,
			totalPages: Math.ceil(rows.length / filter.pageSize),
		};
	},
	async counts(shopId) {
		await sleep(80);
		const rows = ideas.filter((i) => i.status !== "archived" && (!shopId || i.shopId === shopId));
		return {
			all: rows.length,
			moderation: rows.filter((i) => i.status === "moderation").length,
			unpublished: rows.filter((i) => i.status !== "published" && i.status !== "blocked").length,
			blocked: rows.filter((i) => i.status === "blocked").length,
		};
	},
	async shops() {
		await sleep(80);
		return [...new Map(ideas.map((i) => [i.shopId, i.shopName ?? i.shopId])).entries()].map(
			([id, name]) => ({ id, name }),
		);
	},
	async list(filter) {
		await sleep(150);
		const all = [...ideas].sort((a, b) => {
			// Moderation queue first, then published, then the rest; published ideas
			// order by their feed rank so the curated set reads top-down.
			const rank = (s: Idea["status"]) =>
				s === "moderation" ? 0 : s === "published" ? 1 : 2;
			if (rank(a.status) !== rank(b.status)) return rank(a.status) - rank(b.status);
			const fa = a.feedRank ?? Number.POSITIVE_INFINITY;
			const fb = b.feedRank ?? Number.POSITIVE_INFINITY;
			if (fa !== fb) return fa - fb;
			return b.updatedAt.localeCompare(a.updatedAt);
		});
		const visible = all.filter((i) => i.status !== "archived");
		const rows = filter.status === "all" ? visible : visible.filter((i) => i.status === filter.status);
		return Promise.all(rows.map(enrich));
	},

	async get(id) {
		await sleep(120);
		return enrich(find(id));
	},

	async update(id, input) {
		await sleep(180);
		const idea = find(id);
		return save(
			touch(idea, {
				title: input.title?.trim() || idea.title,
				description: input.description ?? idea.description,
				type: input.type ?? idea.type,
				images: input.images ?? idea.images,
				audience: input.audience ?? idea.audience,
				groupDiscount: input.groupDiscount !== undefined ? input.groupDiscount : idea.groupDiscount,
			}),
		);
	},

	async approve(id, note) {
		await sleep(180);
		return save(
			touch(find(id), {
				status: "published",
				rejectionReason: undefined,
				moderatorNote: note?.trim() || undefined,
			}),
		);
	},

	async reject(id, reason) {
		await sleep(180);
		return save(
			touch(find(id), {
				status: "rejected",
				feedRank: null,
				rejectionReason: reason?.trim() || undefined,
				moderatorNote: reason?.trim() || undefined,
			}),
		);
	},

	async block(id, note) {
		await sleep(180);
		return save(
			touch(find(id), { status: "blocked", feedRank: null, moderatorNote: note?.trim() || undefined }),
		);
	},

	async remove(ids) {
		await sleep(150);
		ideas = ideas.map((i) => (ids.includes(i.id) ? touch(i, { status: "archived", feedRank: null }) : i));
	},

	async curate(ids) {
		await sleep(200);
		const now = new Date().toISOString();
		ideas = ideas.map((i) => {
			const rank = ids.indexOf(i.id);
			const feedRank = rank >= 0 ? rank : null;
			return feedRank === i.feedRank ? i : { ...i, feedRank, updatedAt: now };
		});
		return ideas
			.filter((i) => i.feedRank !== null)
			.sort((a, b) => (a.feedRank ?? 0) - (b.feedRank ?? 0));
	},

	async createPlatform(input) {
		await sleep(220);
		const now = new Date().toISOString();
		const productCount = input.images.reduce((n, img) => n + img.pins.length, 0);
		const idea: Idea = {
			id: `idea_platform_${crypto.randomUUID().slice(0, 8)}`,
			shopId: "osmix",
			shopName: "OSMIX",
			title: input.title,
			description: input.description ?? "",
			type: input.type ?? "inspiration",
			status: "published",
			images: input.images.map((img) => ({
				id: img.id,
				url: img.url,
				pins: img.pins.map((p) => ({ ...p })),
			})),
			productCount,
			audience: input.audience ?? [],
			groupDiscount: input.groupDiscount ?? null,
			feedRank: null,
			createdAt: now,
			updatedAt: now,
		};
		ideas = [idea, ...ideas];
		return enrich(idea);
	},

	async uploadPhoto(file) {
		await sleep(200);
		// Офлайн: локальный object-URL, чтобы фото показалось в предпросмотре идеи.
		return URL.createObjectURL(file);
	},
};
